import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Image } from 'antd';
import { Notification } from 'components/CustomNotification.js';
import { ButtonIconView, UserProfile } from './Home.style';
import { CommentList, IconList } from 'assert/OptionList';
import {
  Button,
  UploadFiles,
  DocPreview,
  CloseIcon,
  BrandIconSpinner,
  Select,
} from 'components';
import TextEditor from 'components/TextEditor';
import {
  IconViews,
  ImageContainer,
  PostInfoContainer,
  VideoContainer,
} from 'pages/home/Content.style.js';
import {
  InsertActivityPost,
  InsertNewActivityPost,
  UpdateActivityPostList,
  UpdatePost,
} from 'redux/features/ActivityPostSlice';
import { ReactComponent as ProfileImg } from 'assert/images/Common/profileImg.svg';
import { CountryCode } from 'redux/features/LoginSlice.js';
import { getPostCategoryList } from 'redux/features/DropDownSlice.js';
import { useNavigate } from 'react-router-dom';
import { handleGetMention, hasTextInside } from 'utils/helper';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import VideoPlayer from 'components/Video';
import { MuteProvider } from 'components/MuteContext';

const ModalContent = ({ closePostModal, modalTitle, editData }) => {
  const dispatch = useDispatch();
  const videoRef = useRef();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { postCategoryList } = useSelector((state) => state?.DropdownReducer);

  const [EditDatas, setEditDatas] = useState();
  const [disableBtn, setDisabledBtn] = useState(true);
  const [loading, setLoading] = useState(false);
  const [mentionUserItem, setMentionUserItem] = useState([]);

  const { countryCode } = useSelector((state) => state?.LoginReducer);
  const { getProfile } = useSelector((state) => state?.ProfileSlice);

  const loggedInUserId = getProfile?.userId;
  const [PostDetails, setPostDetails] = useState({
    image: [],
    video: [],
    document: '',
    comment: '',
    userid: loggedInUserId,
    type: '1',
    latitude: 0,
    longitude: 0,
    isComment: true,
    categoryId: 1,
  });

  const disabledImg =
    !modalTitle ||
    PostDetails.image?.length >= 6 ||
    PostDetails.document?.length > 0 ||
    PostDetails.video?.length > 0;
  const disabledDoc =
    !modalTitle ||
    PostDetails.image?.length > 0 ||
    PostDetails.document?.length > 0 ||
    PostDetails.video?.length > 0;
  const disabledVid =
    !modalTitle ||
    PostDetails.image?.length > 0 ||
    PostDetails.document?.length > 0 ||
    PostDetails.video?.length > 0;

  const resetValues = () => {
    Object.keys(PostDetails).forEach((key) => {
      PostDetails[key] = ['image', 'video', 'document'].includes(key) ? [] : '';
    });
    setPostDetails((prev) => ({
      ...prev,
      document: '',
    }));
    closePostModal();
  };

  useEffect(() => {
    !countryCode?.country_code && dispatch(CountryCode());
    postCategoryList?.length === 0 && dispatch(getPostCategoryList());
  }, []);

  useEffect(() => {
    videoRef.current?.load();
    if (PostDetails?.video?.length > 1) {
      setPostDetails((prev) => ({
        ...prev,
        video: [],
      }));
    }
  }, [PostDetails.video]);

  useEffect(() => {
    if (!modalTitle && editData) {
      setEditDatas(editData);
      const { description, images, documents, video, type, comment } = editData;
      setPostDetails((prev) => ({
        ...prev,
        comment: description,
        image: images?.includes(null) ? [] : images,
        video: video && [video],
        document: documents && [documents],
        type,
        isComment: !!comment,
      }));
    }
  }, [editData, modalTitle]);

  const handleDeleteType = useCallback(
    (data, id) => {
      if (data === 'image') {
        setPostDetails((prevState) => ({
          ...prevState,
          image: PostDetails.image?.filter((_, index) => {
            return index !== id;
          }),
        }));
      } else {
        setPostDetails((prevState) => ({
          ...prevState,
          document: '',
          video: [],
        }));
      }
    },
    [PostDetails]
  );

  const getTitle = (icon, title, disabled) => {
    const translatedTitle = t(`HomePage.${title}`);
    return (
      <ButtonIconView disabledbtn={disabled} title={title}>
        <span
          className={clsx(
            title === 'Video' && 'post-video-icon',
            'attachment-icon'
          )}
        >
          {icon}
        </span>
        <div className="icon-title">{translatedTitle}</div>
      </ButtonIconView>
    );
  };

  const handleChange = useCallback((value, type, textLength) => {
    if (type !== 'comment') {
      const fileSizeToMB = value?.[0]?.size / 1024 ** 2;

      if (type === 'image' && value.find((li) => li.size / 1024 ** 2 > 8)) {
        Notification('warning', 'Image size should not exceed 8 MB');
      } else if (type === 'image' && value.length > 6) {
        Notification('warning', 'You cannot upload more than 6 images');
      }
      if (type === 'video' && fileSizeToMB > 50) {
        Notification('warning', 'Video size should not exceed 50 MB');
        return;
      }
      if (type === 'document' && fileSizeToMB > 25) {
        Notification('warning', 'Document size should not exceed 25 MB');
        return;
      }
    }
    if (type === 'comment') {
      if (textLength <= 3000 && textLength !== 0 && hasTextInside(value)) {
        setDisabledBtn(false);
      } else {
        setDisabledBtn(true);
      }
    }

    if (type === 'image') {
      const imgList = [];
      value.forEach(
        (li) =>
          li.size / 1024 ** 2 <= 8 && imgList.length < 6 && imgList.push(li)
      );
      setPostDetails((prevState) => ({
        ...prevState,
        [type]: imgList,
      }));
    } else if (type === 'document') {
      const reader = new FileReader();
      reader.readAsDataURL(value?.[0]);
      reader.onloadend = (e) => {
        setPostDetails((pre) => ({
          ...pre,
          [type]: e.target.result,
          documentSrc: value?.[0],
        }));
      };
    } else {
      setPostDetails((prevState) => ({
        ...prevState,
        [type]: value,
      }));
    }
  }, []);

  const getIcon = (data) => {
    switch (data.title) {
      case 'Photo':
        return (
          <UploadFiles
            handleFileSRC={(data) => handleChange(data, 'image')}
            value={PostDetails.image}
            disabled={disabledImg}
            multiple
            showIcon
            icon={getTitle(IconList[0].icon, 'Photo', disabledImg)}
            accept={{ 'image/jpeg': ['.jpeg', '.png'] }}
          />
        );
      case 'Video':
        return (
          <UploadFiles
            handleFileSRC={(data) => handleChange(data, 'video')}
            value={
              PostDetails.video.length > 0
                ? PostDetails.video
                : PostDetails.video
            }
            disabled={disabledVid}
            showIcon
            icon={getTitle(IconList[1].icon, 'Video', disabledVid)}
            accept={{ 'video/*': [] }}
            type="vedio"
            multiple={false}
          />
        );
      case 'Document':
        return (
          <label htmlFor={`doc-upload`} disabled={disabledDoc}>
            {getTitle(IconList[2].icon, 'Document', disabledDoc)}
            <input
              style={{ display: 'none' }}
              id={`doc-upload`}
              type="file"
              disabled={disabledDoc}
              onChange={(data) => handleChange(data?.target?.files, 'document')}
              onClick={(event) => {
                event.target.value = null;
              }}
              accept="application/pdf"
            />
          </label>
        );
      default:
        return null;
    }
  };

  const submitPost = () => {
    setLoading(true);
    const menionUserId = new Set();

    mentionUserItem.forEach((user) => {
      handleGetMention(PostDetails.comment)?.forEach((userItem) => {
        if ('@' + user.value === userItem) {
          menionUserId.add(user.id);
        }
      });
    });
    const uniqueMentionUserIds = Array.from(menionUserId);

    const sendData = {
      ...PostDetails,
      isComment:
        PostDetails.isComment === 'true' || PostDetails.isComment === true,
      comment: PostDetails.comment?.replace(/&nbsp;&nbsp;+/g, ''),
      mentionInfo: {
        postedByName: getProfile?.username,
        userId: uniqueMentionUserIds,
      },
      country: countryCode?.country_code,
    };
    if (modalTitle) {
      dispatch(InsertActivityPost(sendData))
        .then((res) => {
          if (!res.data) {
            setLoading(false);
            resetValues();
            return null;
          }
          dispatch(InsertNewActivityPost(res?.data?.[0]));
          setLoading(false);
          resetValues();
          Notification(
            'success',
            location.pathname !== '/following' ? (
              <>
                {res?.message + ' '}
                <span
                  className="view-post"
                  onClick={() => navigate('/following')}
                >
                  View Post
                </span>
              </>
            ) : (
              res?.message
            )
          );
        })
        .catch(() => {
          setLoading(false);
        });
    } else {
      dispatch(UpdateActivityPostList(sendData, EditDatas?.id))
        .then((res) => {
          dispatch(UpdatePost(res?.data?.data?.[0]));
          setLoading(false);
          resetValues();
          Notification('success', res?.data?.message);
        })
        .catch((err) => {
          setLoading(false);
          Notification('warning', err?.response?.data?.message);
        });
    }
  };

  const videoSrc = useMemo(() => {
    if (!modalTitle) return PostDetails?.video;
    const videoFile = Array.isArray(PostDetails?.video)
      ? PostDetails.video[0]
      : null;
    if (videoFile instanceof File) {
      return URL.createObjectURL(videoFile);
    }
    return '';
  }, [PostDetails?.video, modalTitle]);

  useEffect(() => {
    if (videoSrc?.startsWith?.('blob:')) {
      return () => {
        URL.revokeObjectURL(videoSrc);
      };
    }
  }, [videoSrc]);

  return (
    <PostInfoContainer>
      <BrandIconSpinner loading={loading} />
      <div className="user-block">
        <UserProfile>
          {getProfile?.profileImage ? (
            <img
              src={getProfile?.profileImage}
              className="user-profile"
              alt="profile-picture"
            />
          ) : (
            <ProfileImg />
          )}
        </UserProfile>
        <div className="username-block">
          <div className="user-displayname">{getProfile?.displayName}</div>
          <div className="user-name">{'@' + getProfile?.username}</div>
        </div>
      </div>
      <div className="comment-container-flex">
        <div className="comment-container">
          <Select
            Options={CommentList}
            value={PostDetails.isComment}
            onChange={(data) => {
              handleChange(data.target.value, 'isComment');
              if (
                PostDetails?.comment?.length <= 3000 &&
                PostDetails?.comment?.length !== 0
              ) {
                setDisabledBtn(false);
              }
            }}
          />
        </div>
        {/* {modalTitle && (
          <div className="comment-container">
            <Select
              Options={postCategoryList}
              value={PostDetails.categoryId}
              onChange={(data) => {
                handleChange(data.target.value, 'categoryId');
                if (
                  PostDetails?.comment?.length <= 3000 &&
                  PostDetails?.comment?.length !== 0
                ) {
                  setDisabledBtn(false);
                }
              }}
            />
          </div>
        )} */}
      </div>
      <TextEditor
        customStyle="home-text-editor"
        placeholder={t('HomePage.LetUsKnow')}
        onChange={(data, textLength) =>
          handleChange(data, 'comment', textLength)
        }
        value={PostDetails?.comment}
        enableMention
        enableHashtag
        handleGetUserItem={(data) =>
          setMentionUserItem((prev) => [...prev, data])
        }
      />
      {PostDetails?.image?.length > 0 && (
        <ImageContainer>
          <div className="imgUploadContainer">
            {PostDetails.image.map((data, index) => {
              return (
                <div className="upload-images" key={index}>
                  {modalTitle && (
                    <CloseIcon
                      customStyle={'imageCloseIcon'}
                      onClick={() => handleDeleteType('image', index)}
                    ></CloseIcon>
                  )}
                  {modalTitle && data ? (
                    <Image
                      className="imageItems"
                      src={
                        typeof data !== 'string' && URL.createObjectURL(data)
                      }
                    />
                  ) : (
                    <Image className="imageItems" src={data} />
                  )}
                </div>
              );
            })}
          </div>
        </ImageContainer>
      )}
      {PostDetails?.document !== '' && (
        <DocPreview
          src={modalTitle ? PostDetails?.document : PostDetails?.document?.[0]}
          onDelete={() => handleDeleteType('document')}
          readOnly={!modalTitle}
        />
      )}
      {PostDetails?.video?.length > 0 && (
        <MuteProvider>
          <VideoContainer className="videoContainer">
            <VideoPlayer stopAutoPlay src={videoSrc} />
            {modalTitle && (
              <CloseIcon
                customStyle={'videoCloseIcon'}
                onClick={() => handleDeleteType('video')}
              ></CloseIcon>
            )}
          </VideoContainer>
        </MuteProvider>
      )}
      <IconViews>
        {IconList.map((data, index) => {
          return <div key={index}>{getIcon(data)}</div>;
        })}
      </IconViews>
      <Button
        label={modalTitle ? t('HomePage.Post') : t('HomePage.Update')}
        onClick={submitPost}
        disabled={disableBtn}
        customStyles="btnWidth"
        disableElevation
      />
    </PostInfoContainer>
  );
};

export default ModalContent;
